// NOTE: Keep this file as ts without tsx so it can be used server-side.
import type { ZodTooBigIssue } from 'zod';

// Begrensninger i Saksbehandlingsløsningen i Sharepoint:
export const SHARE_POINT = {
	INPUT_MAX_LENGTH: 255,
	TEXT_AREA_MAX_LENGTH: 63999,
} as const;

// satisfies Record<string, ZodTooBigIssue['type']>
export const ZOD_TOO_BIG_ISSUE_TYPE = {
	ARRAY: 'array',
	STRING: 'string',
	NUMBER: 'number',
	SET: 'set',
	DATE: 'date',
	BIG_INT: 'bigint',
} as const satisfies Record<string, ZodTooBigIssue['type']>;

// CAUTION: If this is changed, the array below must also be updated as it is used in zod.enums
export const COLOR_NAME = {
	BLACK: 'black',
	BLUE: 'blue',
	BROWN: 'brown',
	GOLD: 'gold',
	GRAY: 'gray',
	GREEN: 'green',
	LIGHT_BROWN: 'lightBrown',
	LIGHT_GRAY: 'lightGray',
	ORANGE: 'orange',
	OTHER: 'other',
	PALE_GOLDENROD: 'paleGoldenrod',
	PINK: 'pink',
	PURPLE: 'purple',
	RED: 'red',
	TRANSPARENT: 'transparent',
	TURQUOISE: 'turquoise',
	WHITE: 'white',
	YELLOW: 'yellow',
} as const;

// Note: Used in zod.enums
export const COLOR_VALUES = [
	COLOR_NAME.BLACK,
	COLOR_NAME.BLUE,
	COLOR_NAME.BROWN,
	COLOR_NAME.GOLD,
	COLOR_NAME.GRAY,
	COLOR_NAME.GREEN,
	COLOR_NAME.LIGHT_BROWN,
	COLOR_NAME.LIGHT_GRAY,
	COLOR_NAME.ORANGE,
	COLOR_NAME.OTHER,
	COLOR_NAME.PALE_GOLDENROD,
	COLOR_NAME.PINK,
	COLOR_NAME.PURPLE,
	COLOR_NAME.RED,
	COLOR_NAME.TRANSPARENT,
	COLOR_NAME.TURQUOISE,
	COLOR_NAME.WHITE,
	COLOR_NAME.YELLOW,
] as const;

export type ColorName = typeof COLOR_VALUES[number];


export const CSS_COLOR_CLASS = {
	[COLOR_NAME.BLACK]: 'text-black',
	[COLOR_NAME.BLUE]: 'text-blue',
	[COLOR_NAME.BROWN]: 'text-brown',
	[COLOR_NAME.GOLD]: 'text-gold',
	[COLOR_NAME.GRAY]: 'text-gray',
	[COLOR_NAME.GREEN]: 'text-green',
	[COLOR_NAME.LIGHT_BROWN]: 'text-lightbrown',
	[COLOR_NAME.LIGHT_GRAY]: 'text-lightgray',
	[COLOR_NAME.ORANGE]: 'text-orange',
	[COLOR_NAME.PALE_GOLDENROD]: 'text-palegoldenrod',
	[COLOR_NAME.PINK]: 'text-pink',
	[COLOR_NAME.PURPLE]: 'text-purple',
	[COLOR_NAME.RED]: 'text-red',
	[COLOR_NAME.TRANSPARENT]: 'text-transparent',
	[COLOR_NAME.TURQUOISE]: 'text-turquoise',
	[COLOR_NAME.WHITE]: 'text-white',
	[COLOR_NAME.YELLOW]: 'text-yellow',
} as const;


export const NOTIFICATION_TYPE = {
	ANONYMOUS: 'anonymous',
	SERVICES: 'services',
} as const;
export type NotificationType = typeof NOTIFICATION_TYPE[keyof typeof NOTIFICATION_TYPE];

// CAUTION: If this is changed, the array below must also be updated, since it's used in zod.enums.
export const PILL_SHAPE = {
	CIRCLE: 'circle',
	DIAMOND: 'diamond',
	OTHER: 'other',
	ROUNDED_RECTANGLE: 'roundedRectangle',
	SQUARE: 'square',
	TRIANGLE: 'triangle',
} as const;

// NOTE: Used in zod.enums
export const PILL_SHAPE_VALUES = [
	PILL_SHAPE.CIRCLE,
	PILL_SHAPE.DIAMOND,
	PILL_SHAPE.OTHER,
	PILL_SHAPE.ROUNDED_RECTANGLE,
	PILL_SHAPE.SQUARE,
	PILL_SHAPE.TRIANGLE,
] as const;

export type PillShape = typeof PILL_SHAPE_VALUES[number];

// CAUTION: If this is changed, the array below must also be updated, since it's used in zod.enums.
export const SUBSTANCE = {
	MIXTURE: 'blanding', // This must match the IntoxicantContent._name
	OTHER: 'annet', // This must match the IntoxicantContent._name
} as const;


// CAUTION: If this is changed, the array below must also be updated, since it's used in zod.enums.
export const SUBSTANCE_FORM = {
	CAPSULE: 'capsule',
	CRYSTAL: 'crystal',
	LIQUID: 'liquid',
	NASAL_SPRAY: 'nasalSpray',
	OTHER: 'other',
	PILL: 'pill',
	POWDER: 'powder',
} as const;

// NOTE: Used in zod.enums
export const SUBSTANCE_FORM_VALUES = [
	SUBSTANCE_FORM.CAPSULE,
	SUBSTANCE_FORM.CRYSTAL,
	SUBSTANCE_FORM.LIQUID,
	SUBSTANCE_FORM.NASAL_SPRAY,
	SUBSTANCE_FORM.OTHER,
	SUBSTANCE_FORM.PILL,
	SUBSTANCE_FORM.POWDER,
] as const;
export type SubstanceForm = typeof SUBSTANCE_FORM_VALUES[number];

// One of multiple predefined values.
// Can also be union of literals.
export const COMMON_ENUM_FIELD_NAME = {
	CAPSULE_POWDER_COLOR: 'capsulePowderColor', // TODO since it's an array add an S on the end?
	COLOR: 'color',
	PILL_SHAPE: 'pillShape',
	SUBSTANCE: 'substance',
	SUBSTANCE_FORM: 'substanceForm',
} as const;

export const COMMON_FREETEXT_FIELD_NAME = {
	ADDITIONAL_INFORMATION: 'additionalInformation',
	CAPSULE_POWDER_COLOR_OTHER: 'capsulePowderColorOther',
	COLOR_OTHER: 'colorOther',
	INTOXICANT_DESCRIPTION: 'intoxicantDescription',
	PILL_SHAPE_OTHER: 'pillShapeOther',
	SUBSTANCE_OTHER: 'substanceOther',
	SUBSTANCE_DETAILS: 'substanceDetails',
	WHERE_OTHER: 'whereOther',
} as const;

export const COMMON_FREETEXT_FIELDS = [
	COMMON_FREETEXT_FIELD_NAME.ADDITIONAL_INFORMATION,
	COMMON_FREETEXT_FIELD_NAME.CAPSULE_POWDER_COLOR_OTHER,
	COMMON_FREETEXT_FIELD_NAME.COLOR_OTHER,
	COMMON_FREETEXT_FIELD_NAME.INTOXICANT_DESCRIPTION,
	COMMON_FREETEXT_FIELD_NAME.PILL_SHAPE_OTHER,
	COMMON_FREETEXT_FIELD_NAME.SUBSTANCE_DETAILS,
	COMMON_FREETEXT_FIELD_NAME.SUBSTANCE_OTHER,
	COMMON_FREETEXT_FIELD_NAME.WHERE_OTHER,
] as const;

// Exactly one specific value
export const COMMON_LITERAL_FIELD_NAME = {
	TYPE: 'type',
} as const;

export const COMMON_DERIVED_FIELD_NAME = {
	COUNTY_CODE: 'countyCode', // From MUNICIPALITY_NAME
	COUNTY_DESCRIPTION: 'countyDescription', // From COUNTY_CODE
	MUNICIPALITY_NAME: 'municipalityName', // From COMMON_DYNAMIC_FIELD_NAME.WHERE
	SUBMISSION_DATE: 'submissionDate', // From new Date()
	IMAGES: 'imagesTmp', // Outside the schema
} as const;

export const COMMON_FIELD_NAME = {
	...COMMON_ENUM_FIELD_NAME,
	...COMMON_DERIVED_FIELD_NAME,
	// ...COMMON_DYNAMIC_FIELD_NAME,
	...COMMON_FREETEXT_FIELD_NAME,
	...COMMON_LITERAL_FIELD_NAME,
} as const;


// This should "mostly" match the zodSchema and JSON.schema.
export interface CommonFormSchema {
	[COMMON_FIELD_NAME.ADDITIONAL_INFORMATION]?: string;
	[COMMON_FIELD_NAME.CAPSULE_POWDER_COLOR_OTHER]?: string;
	[COMMON_FIELD_NAME.CAPSULE_POWDER_COLOR]?: ColorName[];
	[COMMON_FIELD_NAME.COLOR_OTHER]?: string;
	[COMMON_FIELD_NAME.COLOR]?: ColorName[];
	[COMMON_FIELD_NAME.COUNTY_CODE]?: string;
	[COMMON_FIELD_NAME.COUNTY_DESCRIPTION]?: string;
	[COMMON_FIELD_NAME.IMAGES]?: string[]; // File[]
	[COMMON_FIELD_NAME.INTOXICANT_DESCRIPTION]?: string;
	[COMMON_FIELD_NAME.MUNICIPALITY_NAME]?: string;
	[COMMON_FIELD_NAME.PILL_SHAPE_OTHER]?: string;
	[COMMON_FIELD_NAME.PILL_SHAPE]?: PillShape;
	[COMMON_FIELD_NAME.SUBMISSION_DATE]?: string; // Date?
	[COMMON_FIELD_NAME.SUBSTANCE_FORM]?: SubstanceForm;
	[COMMON_FIELD_NAME.SUBSTANCE_OTHER]?: string;
	[COMMON_FIELD_NAME.SUBSTANCE]: string; // Now comes from Content.
	[COMMON_FIELD_NAME.WHERE_OTHER]?: string;
}

export const SUBSTANCE_LEGEND_ID = 'substanceLegend';
export const WHERE_LEGEND_ID = 'whereLegend';
